import {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Gamepad2, Images, Palette, Sparkles, Type, Wand2} from 'lucide-react';
import {
  Button,
  ErrorPlaceholder,
  JobLoader,
  useJobsSimulationConfiguration,
  type StepDefinition,
} from '@tinct/web-components';
import {useFunnel} from '@tinct/web-funnel';
import {useStaticAssetsUrl} from '@tinct/core-utils';
import {useCurrentWorkspaceUrlPrefix} from '@tinct/core-workspace';
import {useGetCampaignById, useStartLandingPageGeneration} from '@tinct/core-api';
import {GameModal, JobAlertBar} from '@tinct/web-layout';
import {CampaignCreationFunnelState} from '../../campaign-creation-funnel/campaign-creation-funnel';

// Simulated sub-steps for the ~20 min generation run. Unlike analyze there is
// no backend job to track — landingPage.status is the only real signal — so
// the loader is pure simulation, completed/failed by the poll below.
const buildingSteps: StepDefinition[] = [
  {title: 'Design system', icon: <Palette />, duration: 180, strategy: 'linear'},
  {title: 'Content blocks', icon: <Type />, duration: 240, strategy: 'linear'},
  {title: 'Visual elements', icon: <Images />, duration: 240, strategy: 'linear'},
  {title: 'Creating your template', icon: <Sparkles />, duration: 300, strategy: 'linear'},
  {title: 'Auto analyse & auto correct', icon: <Wand2 />, duration: 240, strategy: 'exponentialBackoff'},
];

export const Step2bBuilding = () => {
  const {
    state: {campaign: funnelCampaign},
    navigateToNextStep,
  } = useFunnel<CampaignCreationFunnelState>();
  const campaignId = funnelCampaign.id || '';

  const navigate = useNavigate();
  const workspaceUrl = useCurrentWorkspaceUrlPrefix();
  const assetsUrl = useStaticAssetsUrl();
  const [gameOpen, setGameOpen] = useState(false);

  const {data: campaign} = useGetCampaignById(
    {
      id: campaignId,
      fields: {
        id: null,
        landingPage: {
          fields: {
            id: null,
            status: null,
            updatedAt: null,
            currentVersion: {
              fields: {
                id: null,
              },
            },
          },
        },
      },
    },
    {
      refetchInterval: 3000,
      enabled: !!campaignId,
    }
  );

  const landingPage = campaign?.landingPage;
  const isFailed = landingPage?.status === 'failed';
  // Two-signal readiness: the HTML materializes first (status flips to ready),
  // block derivation lands slightly later as currentVersion. Gating on both
  // avoids advancing into a blocks-less editor.
  const isReady = landingPage?.status === 'ready' && !!landingPage.currentVersion;

  const {mutate: startGeneration} = useStartLandingPageGeneration();

  // Kick off the run when the campaign has no page yet (mirrors analyze's
  // "start if lastAnalysisJob === null"). A 409 "run already in flight" —
  // e.g. configure already started it — is fine to swallow: the poll above
  // owns the outcome either way.
  const hasStartedRef = useRef(false);
  useEffect(() => {
    if (hasStartedRef.current) return;
    if (campaign && campaign.landingPage === null) {
      hasStartedRef.current = true;
      startGeneration({id: campaignId});
    }
  }, [campaign, campaignId, startGeneration]);

  useEffect(() => {
    if (isReady) {
      navigateToNextStep(true);
    }
  }, [isReady, navigateToNextStep]);

  const {subJobs} = useJobsSimulationConfiguration({
    steps: buildingSteps,
    isAnalysisCompleted: isReady,
    // Approximates the run's start: the status flip to `generating` bumps it.
    startedAt: landingPage?.updatedAt ?? undefined,
  });

  if (isFailed) {
    return (
      <ErrorPlaceholder
        image={
          <img
            src={`${assetsUrl}/error-analyze.png`}
            alt=""
            className="h-[140px] w-[280px] object-cover"
          />
        }
        title="We couldn't build your page"
        description="Something went wrong while generating your landing page. Your brief and brand are safe — retry the generation, or contact us if it keeps failing."
        actions={
          <>
            <Button size="sm" onClick={() => startGeneration({id: campaignId})}>
              Retry generation
            </Button>
            <Button size="sm" variant="outline" asChild>
              <a href="mailto:support@tinct.ai">Contact support</a>
            </Button>
          </>
        }
      />
    );
  }

  return (
    <div className="flex w-full flex-1 flex-col">
      {/* No job id exists for generation, so the analyze step's store-driven
          alert bar is rendered inline with the same options instead. */}
      <JobAlertBar
        alert={{
          message: 'You can stay (and play?) or let it run in the background.',
          variant: 'info',
          dismissible: false,
          actions: [
            {
              label: <Gamepad2 className="size-4" />,
              onClick: () => setGameOpen(true),
              variant: 'outline',
              size: 'icon',
              dismissOnClick: false,
            },
            {
              label: 'Let it run in background',
              onClick: () => {
                navigate(`${workspaceUrl}/dashboard`, {replace: true});
              },
              variant: 'default',
            },
          ],
        }}
      />
      <div className="container mx-auto flex w-full flex-1 flex-col items-center justify-center">
        <div className="flex w-full max-w-xl flex-col gap-8">
          <div className="flex items-end gap-4">
            <div className="flex flex-1 flex-col gap-2">
              <h2 className="font-heading text-3xl font-semibold tracking-tight text-card-foreground">
                Building your landing page...
              </h2>
              <p className="text-sm text-muted-foreground">
                Estimated time: ~20 min
              </p>
            </div>
            <div className="flex shrink-0 items-end">
              <img
                src={`${assetsUrl}/run-sampling.png`}
                alt=""
                className="w-[100px]"
              />
            </div>
          </div>

          <JobLoader subJobs={subJobs} />
        </div>
        <GameModal open={gameOpen} onOpenChange={setGameOpen} />
      </div>
    </div>
  );
};
